import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  hero: {
    position: "relative",
    overflow: "hidden",
    padding: "24px 26px",
    borderRadius: 20,
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg,#071329 0%,#0B2C68 52%,#155EEF 100%)",
    boxShadow:
      "0 18px 42px rgba(15,43,94,.20)"
  },

  heroTop: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    flexWrap: "wrap"
  },

  eyebrow: {
    color: "#BFDBFE",
    fontSize: "0.66rem",
    fontWeight: 900,
    letterSpacing: ".1em",
    textTransform: "uppercase"
  },

  title: {
    margin: "8px 0 0",
    fontSize: "1.55rem",
    fontWeight: 950,
    letterSpacing: "-.04em"
  },

  subtitle: {
    maxWidth: 650,
    marginTop: 8,
    color: "rgba(219,234,254,.76)",
    fontSize: "0.76rem",
    lineHeight: 1.65
  },

  versionBox: {
    minWidth: 180,
    padding: "15px 17px",
    borderRadius: 15,
    background: "rgba(255,255,255,.10)",
    border: "1px solid rgba(255,255,255,.14)",
    backdropFilter: "blur(10px)"
  },

  versionLabel: {
    color: "rgba(219,234,254,.68)",
    fontSize: "0.62rem",
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".06em"
  },

  versionValue: {
    marginTop: 7,
    fontSize: "1.35rem",
    fontWeight: 950
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, .9fr) minmax(0, 1.25fr)",
    gap: 18,

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  card: {
    minWidth: 0,
    padding: 21,
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow:
      "0 8px 28px rgba(15,23,42,.05)"
  },

  cardTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.94rem",
    fontWeight: 900
  },

  cardSubtitle: {
    marginTop: 5,
    color: "#94A3B8",
    fontSize: "0.7rem",
    lineHeight: 1.55
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 15
  },

  label: {
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.68rem",
    fontWeight: 850
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 42,
    padding: "10px 12px",
    borderRadius: 11,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.10)"
        : "#DCE4EF"
    }`,
    outline: "none",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#F8FAFC",
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontFamily: "inherit",
    fontSize: "0.75rem",

    "&:focus": {
      borderColor: "#3B82F6",
      boxShadow:
        "0 0 0 3px rgba(59,130,246,.10)"
    }
  },

  textarea: {
    minHeight: 100,
    resize: "vertical"
  },

  switches: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 15,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  checkCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    padding: 12,
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#E8EDF5"
    }`
  },

  checkText: {
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.68rem",
    lineHeight: 1.45
  },

  primaryButton: {
    width: "100%",
    minHeight: 42,
    marginTop: 17,
    border: 0,
    borderRadius: 11,
    background:
      "linear-gradient(135deg,#2563EB,#4F46E5)",
    color: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 900,
    boxShadow:
      "0 10px 22px rgba(37,99,235,.22)",

    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed"
    }
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 11,
    marginTop: 17
  },

  releaseItem: {
    padding: 15,
    borderRadius: 14,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#E8EDF5"
    }`
  },

  releaseHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap"
  },

  releaseVersion: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.9rem",
    fontWeight: 950
  },

  releaseTitle: {
    marginTop: 4,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.74rem",
    fontWeight: 750
  },

  releaseMeta: {
    marginTop: 5,
    color: "#94A3B8",
    fontSize: "0.64rem"
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: "0.61rem",
    fontWeight: 900
  },

  changelog: {
    margin: "12px 0 0",
    paddingLeft: 18,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.68rem",
    lineHeight: 1.65
  },

  publishButton: {
    minHeight: 34,
    marginTop: 12,
    padding: "7px 11px",
    borderRadius: 9,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.08)",
    color: "#2563EB",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.66rem",
    fontWeight: 900,

    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed"
    }
  },

  empty: {
    marginTop: 18,
    padding: 26,
    borderRadius: 14,
    textAlign: "center",
    color: "#94A3B8",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.02)"
        : "#F8FAFC",
    fontSize: "0.72rem"
  }
}));

const initialForm = {
  version: "",
  title: "",
  description: "",
  changelog: "",
  mandatory: false,
  showAnnouncement: true
};

const formatDateTime = value => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo"
  });
};

const statusPresentation = status => {
  if (status === "published") {
    return {
      label: "Publicada",
      color: "#15803D",
      background: "rgba(34,197,94,.12)"
    };
  }

  if (status === "archived") {
    return {
      label: "Arquivada",
      color: "#64748B",
      background: "rgba(100,116,139,.12)"
    };
  }

  return {
    label: "Rascunho",
    color: "#B45309",
    background: "rgba(245,158,11,.13)"
  };
};

const ReleasesModule = () => {
  const classes = useStyles();

  const [current, setCurrent] = useState(null);
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(initialForm);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] =
    useState(null);

  const loadReleases = useCallback(async () => {
    setLoading(true);

    try {
      const [
        currentResponse,
        releasesResponse
      ] = await Promise.all([
        api.get("/releases/current"),
        api.get("/dashboard-master/releases")
      ]);

      setCurrent(currentResponse.data);
      setReleases(
        Array.isArray(releasesResponse.data)
          ? releasesResponse.data
          : []
      );
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  const draftCount = useMemo(
    () =>
      releases.filter(
        item => item.status === "draft"
      ).length,
    [releases]
  );

  const handleChange = event => {
    const {
      name,
      value,
      type,
      checked
    } = event.target;

    setForm(previous => ({
      ...previous,
      [name]:
        type === "checkbox"
          ? checked
          : value
    }));
  };

  const handleCreate = async event => {
    event.preventDefault();

    if (
      !form.version.trim() ||
      !form.title.trim()
    ) {
      toast.error(
        "Informe a versão e o título da release."
      );
      return;
    }

    setSaving(true);

    try {
      await api.post(
        "/dashboard-master/releases",
        form
      );

      toast.success(
        "Rascunho da release criado."
      );

      setForm(initialForm);
      await loadReleases();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async release => {
    const confirmed = window.confirm(
      `Deseja publicar a versão ${release.version}?`
    );

    if (!confirmed) {
      return;
    }

    setPublishingId(release.id);

    try {
      await api.post(
        `/dashboard-master/releases/${release.id}/publish`
      );

      toast.success(
        `Versão ${release.version} publicada com sucesso.`
      );

      await loadReleases();
    } catch (error) {
      toastError(error);
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.heroTop}>
          <div>
            <div className={classes.eyebrow}>
              Korax Release Center
            </div>

            <h2 className={classes.title}>
              Central de Releases
            </h2>

            <div className={classes.subtitle}>
              Crie, revise e publique novas versões da
              plataforma. A publicação atualiza a versão
              oficial e prepara a comunicação para todos
              os aplicativos Korax.
            </div>
          </div>

          <div className={classes.versionBox}>
            <div className={classes.versionLabel}>
              Versão em produção
            </div>

            <div className={classes.versionValue}>
              v{current?.version || "—"}
            </div>

            <div
              style={{
                marginTop: 5,
                color: "rgba(219,234,254,.65)",
                fontSize: "0.63rem"
              }}
            >
              {current?.release?.publishedAt
                ? formatDateTime(
                    current.release.publishedAt
                  )
                : "Sem data de publicação"}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.grid}>
        <form
          className={classes.card}
          onSubmit={handleCreate}
        >
          <div className={classes.cardTitle}>
            Criar nova release
          </div>

          <div className={classes.cardSubtitle}>
            A release será criada como rascunho e poderá
            ser publicada após a revisão.
          </div>

          <div className={classes.field}>
            <label className={classes.label}>
              Versão
            </label>

            <input
              className={classes.input}
              name="version"
              value={form.version}
              onChange={handleChange}
              placeholder="Ex.: 15.0.3"
            />
          </div>

          <div className={classes.field}>
            <label className={classes.label}>
              Título
            </label>

            <input
              className={classes.input}
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Ex.: Melhorias no aplicativo Korax"
            />
          </div>

          <div className={classes.field}>
            <label className={classes.label}>
              Descrição
            </label>

            <textarea
              className={`${classes.input} ${classes.textarea}`}
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Resumo executivo da atualização."
            />
          </div>

          <div className={classes.field}>
            <label className={classes.label}>
              Changelog
            </label>

            <textarea
              className={`${classes.input} ${classes.textarea}`}
              name="changelog"
              value={form.changelog}
              onChange={handleChange}
              placeholder={
                "Uma novidade por linha.\nEx.: Novo dashboard\nCorreção do login PWA"
              }
            />
          </div>

          <div className={classes.switches}>
            <label className={classes.checkCard}>
              <input
                type="checkbox"
                name="mandatory"
                checked={form.mandatory}
                onChange={handleChange}
              />

              <span className={classes.checkText}>
                <strong>
                  Atualização obrigatória
                </strong>
                <br />
                O usuário deverá atualizar para continuar.
              </span>
            </label>

            <label className={classes.checkCard}>
              <input
                type="checkbox"
                name="showAnnouncement"
                checked={form.showAnnouncement}
                onChange={handleChange}
              />

              <span className={classes.checkText}>
                <strong>
                  Exibir comunicado
                </strong>
                <br />
                Mostrar as novidades ao usuário.
              </span>
            </label>
          </div>

          <button
            type="submit"
            className={classes.primaryButton}
            disabled={saving}
          >
            {saving
              ? "Criando rascunho..."
              : "Criar release"}
          </button>
        </form>

        <div className={classes.card}>
          <div className={classes.cardTitle}>
            Histórico de releases
          </div>

          <div className={classes.cardSubtitle}>
            {draftCount} rascunho(s) aguardando publicação.
          </div>

          {loading ? (
            <div className={classes.empty}>
              Carregando releases...
            </div>
          ) : releases.length === 0 ? (
            <div className={classes.empty}>
              Nenhuma release registrada.
            </div>
          ) : (
            <div className={classes.list}>
              {releases.map(release => {
                const presentation =
                  statusPresentation(
                    release.status
                  );

                return (
                  <div
                    key={release.id}
                    className={classes.releaseItem}
                  >
                    <div
                      className={classes.releaseHeader}
                    >
                      <div>
                        <div
                          className={classes.releaseVersion}
                        >
                          v{release.version}
                        </div>

                        <div
                          className={classes.releaseTitle}
                        >
                          {release.title}
                        </div>

                        <div
                          className={classes.releaseMeta}
                        >
                          Criada em{" "}
                          {formatDateTime(
                            release.createdAt
                          )}
                          {release.publishedAt
                            ? ` • Publicada em ${formatDateTime(
                                release.publishedAt
                              )}`
                            : ""}
                        </div>
                      </div>

                      <span
                        className={classes.badge}
                        style={{
                          color:
                            presentation.color,
                          background:
                            presentation.background
                        }}
                      >
                        {presentation.label}
                      </span>
                    </div>

                    {release.description && (
                      <div
                        className={classes.releaseMeta}
                        style={{
                          marginTop: 10,
                          lineHeight: 1.55
                        }}
                      >
                        {release.description}
                      </div>
                    )}

                    {Array.isArray(
                      release.changelog
                    ) &&
                      release.changelog.length > 0 && (
                        <ul className={classes.changelog}>
                          {release.changelog.map(
                            (item, index) => (
                              <li key={`${release.id}-${index}`}>
                                {item}
                              </li>
                            )
                          )}
                        </ul>
                      )}

                    {release.mandatory && (
                      <span
                        className={classes.badge}
                        style={{
                          marginTop: 10,
                          color: "#B91C1C",
                          background:
                            "rgba(239,68,68,.11)"
                        }}
                      >
                        Obrigatória
                      </span>
                    )}

                    {release.status === "draft" && (
                      <button
                        type="button"
                        className={classes.publishButton}
                        disabled={
                          publishingId === release.id
                        }
                        onClick={() =>
                          handlePublish(release)
                        }
                      >
                        {publishingId === release.id
                          ? "Publicando..."
                          : "Publicar release"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleasesModule;
